import {
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import type { BrowserWorkflowOutput } from '@/types/browserAutomation';

import { RetrieveResultsSection } from './RetrieveResultsSection';
import { SearchResultsSection } from './SearchResultsSection';

interface BrowserRun {
  id: string;
  police_report_id: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  portal_vendor: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  found_report: boolean;
  report_ready: boolean;
  report_url: string | null;
  error_message: string | null;
  workflow_id: string | null;
  request_payload: Record<string, unknown> | null;
  result_data: BrowserWorkflowOutput | Record<string, unknown>;
}

interface RunDetailProps {
  run: BrowserRun;
}

const StatusBadge = ({ status }: { status: BrowserRun['status'] }) => {
  const config = {
    scheduled: {
      icon: <ClockIcon className="h-5 w-5" />,
      label: 'Scheduled',
      className: 'bg-gray-100 text-gray-700',
    },
    in_progress: {
      icon: <ClockIcon className="h-5 w-5 animate-spin" />,
      label: 'In Progress',
      className: 'bg-blue-100 text-blue-700',
    },
    completed: {
      icon: <CheckCircleIcon className="h-5 w-5" />,
      label: 'Completed',
      className: 'bg-green-100 text-green-700',
    },
    failed: {
      icon: <XCircleIcon className="h-5 w-5" />,
      label: 'Failed',
      className: 'bg-red-100 text-red-700',
    },
    cancelled: {
      icon: <ExclamationCircleIcon className="h-5 w-5" />,
      label: 'Cancelled',
      className: 'bg-gray-100 text-gray-600',
    },
  };

  const { icon, label, className } = config[status];

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium ${className}`}
    >
      {icon}
      <span>{label}</span>
    </div>
  );
};

const isBrowserWorkflowOutput = (
  data: BrowserWorkflowOutput | Record<string, unknown>,
): data is BrowserWorkflowOutput => {
  return 'search_response' in data && data.search_response !== undefined;
};

export const RunDetail = ({ run }: RunDetailProps) => {
  const hasStructuredResults = isBrowserWorkflowOutput(run.result_data);
  const workflowOutput = hasStructuredResults
    ? (run.result_data as BrowserWorkflowOutput)
    : null;

  const duration =
    run.started_at && run.completed_at
      ? Math.round(
          (new Date(run.completed_at).getTime() -
            new Date(run.started_at).getTime()) /
            1000,
        )
      : null;

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <StatusBadge status={run.status} />
          {run.portal_vendor && (
            <span className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded font-mono">
              {run.portal_vendor}
            </span>
          )}
        </div>

        {/* Metadata */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-gray-500">Created</div>
            <div className="text-gray-900">
              {new Date(run.created_at).toLocaleString()}
            </div>
          </div>
          {run.started_at && (
            <div>
              <div className="text-gray-500">Started</div>
              <div className="text-gray-900">
                {new Date(run.started_at).toLocaleString()}
              </div>
            </div>
          )}
          {run.completed_at && (
            <div>
              <div className="text-gray-500">Completed</div>
              <div className="text-gray-900">
                {new Date(run.completed_at).toLocaleString()}
              </div>
            </div>
          )}
          {duration !== null && (
            <div>
              <div className="text-gray-500">Duration</div>
              <div className="text-gray-900 font-medium">{duration}s</div>
            </div>
          )}
        </div>
      </div>

      {/* Quick stats for completed runs */}
      {run.status === 'completed' && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-500 mb-1">Report Found</div>
            <div className="text-lg font-semibold text-gray-900">
              {run.found_report ? '✓' : '✗'}
            </div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-500 mb-1">Report Ready</div>
            <div className="text-lg font-semibold text-gray-900">
              {run.report_ready ? '✓' : '✗'}
            </div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-500 mb-1">Retrieved</div>
            <div className="text-lg font-semibold text-gray-900">
              {run.report_url ? '✓' : '✗'}
            </div>
          </div>
        </div>
      )}

      {/* Top-level error */}
      {run.error_message && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-sm font-semibold text-red-900 mb-2">Error</div>
          <div className="text-sm text-red-700">{run.error_message}</div>
        </div>
      )}

      {/* Direct report URL */}
      {run.report_url &&
        !workflowOutput?.retrieve_response?.data.record_s3_url && (
          <a
            href={run.report_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg p-4 hover:bg-green-100 transition-colors"
          >
            <DocumentTextIcon className="h-6 w-6 text-green-600" />
            <div>
              <div className="text-sm font-semibold text-green-900">
                Report Retrieved
              </div>
              <div className="text-xs text-green-700 break-all">
                {run.report_url}
              </div>
            </div>
          </a>
        )}

      {/* Workflow results */}
      {workflowOutput && (
        <div className="space-y-6">
          <SearchResultsSection searchOutput={workflowOutput.search_response} />
          {workflowOutput.retrieve_response && (
            <RetrieveResultsSection
              retrieveOutput={workflowOutput.retrieve_response}
            />
          )}
        </div>
      )}

      {/* Technical details */}
      <details className="border border-gray-200 rounded-lg">
        <summary className="cursor-pointer p-3 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50">
          Technical Details
        </summary>
        <div className="p-4 border-t border-gray-200 space-y-3 text-xs bg-gray-50">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-gray-500 font-medium mb-1">Run ID</div>
              <div className="font-mono text-gray-900 break-all">{run.id}</div>
            </div>
            {run.workflow_id && (
              <div>
                <div className="text-gray-500 font-medium mb-1">
                  Workflow ID
                </div>
                <div className="font-mono text-gray-900 break-all">
                  {run.workflow_id}
                </div>
              </div>
            )}
          </div>

          {run.request_payload &&
            Object.keys(run.request_payload).length > 0 && (
              <details>
                <summary className="cursor-pointer text-gray-600 hover:text-gray-900 font-medium">
                  Request Payload
                </summary>
                <pre className="mt-2 p-3 bg-white rounded border border-gray-200 text-xs overflow-x-auto">
                  {JSON.stringify(run.request_payload, null, 2)}
                </pre>
              </details>
            )}

          {!hasStructuredResults && Object.keys(run.result_data).length > 0 && (
            <details>
              <summary className="cursor-pointer text-gray-600 hover:text-gray-900 font-medium">
                Raw Result Data
              </summary>
              <pre className="mt-2 p-3 bg-white rounded border border-gray-200 text-xs overflow-x-auto">
                {JSON.stringify(run.result_data, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </details>
    </div>
  );
};
